import { Box } from "@mui/material";
import type { InputHTMLAttributes, JSX, ReactNode } from "react";

/**
 * A labelled bare-input field for the sign-in card. When an `adornment` is
 * given (the password-eye toggle) the input + adornment share one focus frame;
 * otherwise the input carries its own border/focus ring. Extracted verbatim
 * from `LoginPage.tsx`.
 */
export function Field({
  label,
  value,
  onChange,
  adornment,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Optional trailing affordance rendered inside the same visual frame as
   * the input (e.g. the password-eye toggle). */
  adornment?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">): JSX.Element {
  const input = (
    <Box
      component="input"
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={
        adornment
          ? {
              width: "100%",
              background: "transparent",
              border: 0,
              fontSize: 14,
              color: "text.primary",
              "&::placeholder": { color: "text.disabled" },
              "&:focus": { outline: "none" },
            }
          : {
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              px: 1.75,
              py: 1.25,
              fontSize: 14,
              color: "text.primary",
              "&::placeholder": { color: "text.disabled" },
              "&:focus": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: -1 },
            }
      }
    />
  );
  return (
    <Box component="label" sx={{ display: "grid", gap: 0.75, fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
      {label}
      {adornment ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            py: 0.25,
            pl: 1.75,
            pr: 0.75,
            outline: "2px solid transparent",
            transition: "outline-color 100ms",
            "&:focus-within": { outlineColor: "primary.main" },
          }}
        >
          {input}
          {adornment}
        </Box>
      ) : (
        input
      )}
    </Box>
  );
}
